import { ChevronDown, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../hooks/useAuth';
import { localize } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';

interface User {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  role: 'GUEST' | 'HOST' | 'ADMIN';
  status: string;
  isHost: boolean;
  profilesCount: number;
  bookingsCount: number;
  createdAt: string;
}

interface StoreCreditTransaction {
  id: string;
  type: string;
  amount: number;
  description: string | null;
  note: string | null;
  adminId: string | null;
  adminName: string | null;
  createdAt: string;
  booking: {
    id: string;
    totalAmount: number;
    session: {
      activity: { titleCN: string | null; titleEN: string };
    };
  } | null;
}

interface StoreCreditData {
  balance: number;
  transactions: StoreCreditTransaction[];
  pagination: { nextCursor: string | null; hasMore: boolean };
}

interface UserProfilesData {
  children: {
    id: string;
    name: string;
    preferredName: string;
    birthDate: string;
    age: number;
    notes: string | null;
  }[];
  adults: { id: string; name: string; phone: string }[];
}

interface UserBooking {
  id: string;
  status: string;
  totalAmount: number;
  isPassBooking: boolean;
  participantsCount: number;
  sessionStartTime: string;
  activityTitleCN: string;
  activityTitleEN: string;
  createdAt: string;
}

interface UserPassItem {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  status: string;
  totalSessions: number;
  usedSessions: number;
  remainingSessions: number;
  paymentAmount: number;
  refundableAmount: number;
  expiresAt: string;
  purchasedAt: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const formatUsd = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const formatPtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export default function AdminUsers() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'GUEST' | 'HOST' | 'ADMIN'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

        const response = await fetch(`${apiUrl}/admin/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const result = await response.json();
        if (result.success) {
          setUsers(result.data || []);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load users';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isAuthenticated, getToken]);

  const filteredUsers = users.filter((user) => {
    if (filter === 'all') return true;
    return user.role === filter;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout
      title="User Management"
      headerRight={<span className="text-sm text-gray-500">{users.length} registered users</span>}
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div>
          <nav className="flex -mb-px overflow-x-auto">
            {(['all', 'GUEST', 'HOST', 'ADMIN'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap flex-shrink-0 ${
                  filter === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'all' ? 'All' : tab}
                <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                  {tab === 'all' ? users.length : users.filter((u) => u.role === tab).length}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <div className="text-4xl mb-4">👥</div>
            <p>No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profiles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bookings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.name || 'No name'}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      {user.phone && <div className="text-xs text-gray-400">{user.phone}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <RoleBadge role={user.role} isHost={user.isHost} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.profilesCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.bookingsCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        timeZone: 'America/Los_Angeles',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="text-xs text-primary hover:underline"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </AdminLayout>
  );
}

function RoleBadge({ role, isHost }: { role: string; isHost: boolean }) {
  const roleConfig: Record<string, { bg: string; text: string; label: string }> = {
    ADMIN: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Admin' },
    HOST: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Host' },
    GUEST: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Guest' },
  };

  const config = roleConfig[role] || roleConfig.GUEST;

  return (
    <div className="flex items-center gap-1">
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
      {isHost && role !== 'HOST' && (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
          Host
        </span>
      )}
    </div>
  );
}

function UserDetailModal({ user, onClose }: { user: User; onClose: () => void }) {
  const siteLanguage = useSiteLanguage();
  const { getToken } = useAuth();
  const [creditData, setCreditData] = useState<StoreCreditData | null>(null);
  const [creditLoading, setCreditLoading] = useState(true);
  const [showIssueDialog, setShowIssueDialog] = useState(false);

  const fetchStoreCredit = useCallback(async () => {
    try {
      setCreditLoading(true);
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
      const response = await fetch(`${apiUrl}/admin/store-credit/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCreditData(result.data);
        }
      }
    } catch {
      // Silently fail - store credit section will show as loading
    } finally {
      setCreditLoading(false);
    }
  }, [user.id, getToken]);

  useEffect(() => {
    fetchStoreCredit();
  }, [fetchStoreCredit]);

  const handleCreditIssued = () => {
    setShowIssueDialog(false);
    fetchStoreCredit();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">User Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-500">User ID</label>
              <p className="font-mono text-sm">{user.id}</p>
            </div>

            <div>
              <label className="text-sm text-gray-500">Name</label>
              <p className="font-medium">{user.name || 'Not set'}</p>
            </div>

            <div>
              <label className="text-sm text-gray-500">Email</label>
              <p className="text-gray-900">{user.email}</p>
            </div>

            {user.phone && (
              <div>
                <label className="text-sm text-gray-500">Phone</label>
                <p className="text-gray-900">{user.phone}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Role</label>
                <div className="mt-1">
                  <RoleBadge role={user.role} isHost={user.isHost} />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-500">Status</label>
                <p className="text-gray-900 capitalize">{user.status}</p>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-500">Joined</label>
              <p>
                {new Date(user.createdAt).toLocaleString('en-US', {
                  timeZone: 'America/Los_Angeles',
                })}
              </p>
            </div>
          </div>

          {/* Profiles / Bookings / Passes Sections */}
          <div className="mt-6 space-y-3">
            <ProfilesSection user={user} />
            <BookingsSection user={user} onCreditChanged={fetchStoreCredit} />
            <PassesSection user={user} onCreditChanged={fetchStoreCredit} />
          </div>

          {/* Store Credit Section */}
          <div className="mt-6 pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Store Credit</h3>

            {creditLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : creditData ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm text-gray-500">Current Balance</label>
                    <p className="text-xl font-bold text-green-700">
                      ${(creditData.balance / 100).toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowIssueDialog(true)}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    + Issue Credit
                  </button>
                </div>

                {creditData.transactions.length > 0 && (
                  <div>
                    <label className="text-sm text-gray-500 mb-2 block">Recent Transactions</label>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {creditData.transactions.map((tx) => (
                        <div key={tx.id} className="border border-gray-200 rounded-lg p-3 text-sm">
                          <div className="flex items-center justify-between">
                            <span
                              className={`font-semibold ${
                                tx.type === 'CREDIT' ||
                                tx.type === 'ADMIN_CREDIT' ||
                                tx.type === 'GIFT_CARD_REDEMPTION'
                                  ? 'text-green-600'
                                  : 'text-red-600'
                              }`}
                            >
                              {tx.type === 'CREDIT' ||
                              tx.type === 'ADMIN_CREDIT' ||
                              tx.type === 'GIFT_CARD_REDEMPTION'
                                ? '+'
                                : '-'}
                              ${(tx.amount / 100).toFixed(2)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(tx.createdAt).toLocaleDateString('en-US', {
                                timeZone: 'America/Los_Angeles',
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {tx.type}
                            {tx.description ? ` - ${tx.description}` : ''}
                          </div>
                          {tx.booking && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              Booking:{' '}
                              {localize(
                                tx.booking.session.activity.titleCN,
                                tx.booking.session.activity.titleEN,
                                siteLanguage
                              )}
                            </div>
                          )}
                          {tx.note && (
                            <div className="text-xs text-amber-600 mt-0.5">Note: {tx.note}</div>
                          )}
                          {tx.adminName && (
                            <div className="text-xs text-gray-400 mt-0.5">By: {tx.adminName}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Failed to load store credit data</p>
            )}
          </div>

          <div className="mt-6 pt-4">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Issue Credit Dialog */}
      {showIssueDialog && (
        <IssueCreditDialog
          user={user}
          onClose={() => setShowIssueDialog(false)}
          onSuccess={handleCreditIssued}
        />
      )}
    </div>
  );
}

function IssueCreditDialog({
  user,
  onClose,
  onSuccess,
}: {
  user: User;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { getToken } = useAuth();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Generate idempotency key once when dialog opens
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const parsedAmount = parseFloat(amount);
  const amountCents = Math.round(parsedAmount * 100);
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount <= 500;
  const trimmedNote = note.trim();
  const isValidNote = trimmedNote.length >= 5 && trimmedNote.length <= 500;
  const canSubmit = isValidAmount && isValidNote && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setSubmitting(true);
      setError(null);
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

      const response = await fetch(`${apiUrl}/admin/store-credit/issue`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          userId: user.id,
          amount: amountCents,
          note: trimmedNote,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to issue credit');
      }

      onSuccess();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to issue credit';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Issue Store Credit</h3>

          <div className="mb-4">
            <p className="text-sm text-gray-600">
              User: <strong>{user.name || user.email}</strong>{' '}
              <span className="text-gray-400">({user.email})</span>
            </p>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="20.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              />
              {amount && !isValidAmount && (
                <p className="text-xs text-red-500 mt-1">Amount must be $0.01 - $500.00</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note (required, internal only)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. customer complaint compensation"
                rows={3}
                maxLength={500}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <div className="flex justify-between mt-1">
                {note && !isValidNote && (
                  <p className="text-xs text-red-500">Note must be 5-500 characters</p>
                )}
                <p className="text-xs text-gray-400 ml-auto">{trimmedNote.length}/500</p>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700">
              This will send an email to the user notifying them of the credit.
            </p>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {submitting
                ? 'Issuing...'
                : isValidAmount
                  ? `Issue $${parsedAmount.toFixed(2)} Credit`
                  : 'Issue Credit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  count,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  count?: number;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg"
      >
        <span className="font-medium text-gray-900">
          {title}
          {count !== undefined && (
            <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
              {count}
            </span>
          )}
        </span>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {expanded && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function SectionSpinner() {
  return (
    <div className="text-center py-4">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
    </div>
  );
}

function BookingStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    CONFIRMED: 'bg-green-100 text-green-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    CANCELLED_BY_HOST: 'bg-orange-100 text-orange-800',
    TRANSFERRED: 'bg-blue-100 text-blue-800',
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
        styles[status] || 'bg-gray-100 text-gray-600'
      }`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function ProfilesSection({ user }: { user: User }) {
  const { getToken } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState<UserProfilesData | null>(null);
  const [loading, setLoading] = useState(false);

  const handleToggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !data && !loading) {
      try {
        setLoading(true);
        const token = await getToken();
        const response = await fetch(`${API_URL}/admin/users/${user.id}/profiles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (result.success) setData(result.data);
      } catch {
        // Section shows empty state on failure
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Section
      title="Profiles"
      count={user.profilesCount}
      expanded={expanded}
      onToggle={handleToggle}
    >
      {loading ? (
        <SectionSpinner />
      ) : !data ? (
        <p className="text-sm text-gray-500">Failed to load profiles</p>
      ) : data.children.length === 0 && data.adults.length === 0 ? (
        <p className="text-sm text-gray-500">No profiles</p>
      ) : (
        <div className="space-y-2">
          {data.children.map((p) => (
            <div key={p.id} className="border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">
                  {p.name}
                  {p.preferredName && p.preferredName !== p.name && (
                    <span className="text-gray-500 font-normal"> ({p.preferredName})</span>
                  )}
                </span>
                <span className="text-xs bg-pink-100 text-pink-800 px-2 py-0.5 rounded-full">
                  Kid, age {p.age}
                </span>
              </div>
              {p.notes && <div className="text-xs text-gray-500 mt-1">Notes: {p.notes}</div>}
            </div>
          ))}
          {data.adults.map((p) => (
            <div key={p.id} className="border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{p.name}</span>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                  Adult
                </span>
              </div>
              {p.phone && <div className="text-xs text-gray-500 mt-1">{p.phone}</div>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function BookingsSection({ user, onCreditChanged }: { user: User; onCreditChanged: () => void }) {
  const siteLanguage = useSiteLanguage();
  const { getToken } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [bookings, setBookings] = useState<UserBooking[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refundTarget, setRefundTarget] = useState<UserBooking | null>(null);

  const fetchPage = useCallback(
    async (cursor: string | null) => {
      try {
        setLoading(true);
        const token = await getToken();
        const params = new URLSearchParams({ limit: '10' });
        if (cursor) params.set('cursor', cursor);
        const response = await fetch(`${API_URL}/admin/users/${user.id}/bookings?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (result.success) {
          setBookings((prev) => (cursor ? [...prev, ...result.data] : result.data));
          setNextCursor(result.pagination.hasMore ? result.pagination.nextCursor : null);
          setLoaded(true);
        }
      } catch {
        // Section shows empty state on failure
      } finally {
        setLoading(false);
      }
    },
    [user.id, getToken]
  );

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !loaded && !loading) fetchPage(null);
  };

  const handleRefunded = () => {
    setRefundTarget(null);
    fetchPage(null); // reload first page so the refunded status shows
    onCreditChanged();
  };

  return (
    <Section
      title="Bookings"
      count={user.bookingsCount}
      expanded={expanded}
      onToggle={handleToggle}
    >
      {!loaded && loading ? (
        <SectionSpinner />
      ) : !loaded ? (
        <p className="text-sm text-gray-500">Failed to load bookings</p>
      ) : bookings.length === 0 ? (
        <p className="text-sm text-gray-500">No bookings</p>
      ) : (
        <div className="space-y-2">
          {bookings.map((b) => (
            <div key={b.id} className="border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-900 truncate">
                  {localize(b.activityTitleCN, b.activityTitleEN, siteLanguage)}
                </span>
                <BookingStatusBadge status={b.status} />
              </div>
              <div className="flex items-center justify-between mt-1">
                <div className="text-xs text-gray-500">
                  {formatPtDate(b.sessionStartTime)} | {b.participantsCount}{' '}
                  {b.participantsCount === 1 ? 'participant' : 'participants'} |{' '}
                  {b.isPassBooking ? 'Pass booking' : formatUsd(b.totalAmount)}
                </div>
                {b.status === 'CONFIRMED' && (
                  <button
                    onClick={() => setRefundTarget(b)}
                    className="text-xs text-red-600 hover:underline whitespace-nowrap"
                  >
                    Refund
                  </button>
                )}
              </div>
            </div>
          ))}
          {nextCursor && (
            <button
              onClick={() => fetchPage(nextCursor)}
              disabled={loading}
              className="w-full py-2 text-sm text-primary hover:bg-gray-50 rounded-lg border border-gray-200 disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Load more'}
            </button>
          )}
        </div>
      )}

      {refundTarget && (
        <RefundDialog
          title="Refund Booking"
          summary={`${localize(refundTarget.activityTitleCN, refundTarget.activityTitleEN, siteLanguage)}, ${formatPtDate(refundTarget.sessionStartTime)}`}
          amountCents={refundTarget.isPassBooking ? 0 : refundTarget.totalAmount}
          zeroAmountHint="This is a pass booking. Refunding restores the pass sessions instead of issuing store credit."
          url={`${API_URL}/admin/bookings/${refundTarget.id}/refund`}
          onClose={() => setRefundTarget(null)}
          onSuccess={handleRefunded}
        />
      )}
    </Section>
  );
}

function PassesSection({ user, onCreditChanged }: { user: User; onCreditChanged: () => void }) {
  const siteLanguage = useSiteLanguage();
  const { getToken } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [passes, setPasses] = useState<UserPassItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [refundTarget, setRefundTarget] = useState<UserPassItem | null>(null);

  const fetchPasses = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await fetch(`${API_URL}/admin/users/${user.id}/passes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.success) setPasses(result.data);
    } catch {
      // Section shows empty state on failure
    } finally {
      setLoading(false);
    }
  }, [user.id, getToken]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !passes && !loading) fetchPasses();
  };

  const handleRefunded = () => {
    setRefundTarget(null);
    fetchPasses();
    onCreditChanged();
  };

  return (
    <Section title="Passes" expanded={expanded} onToggle={handleToggle}>
      {loading ? (
        <SectionSpinner />
      ) : !passes ? (
        <p className="text-sm text-gray-500">Failed to load passes</p>
      ) : passes.length === 0 ? (
        <p className="text-sm text-gray-500">No passes</p>
      ) : (
        <div className="space-y-2">
          {passes.map((p) => (
            <div key={p.id} className="border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-900 truncate">
                  {localize(p.titleCN, p.titleEN, siteLanguage)}
                </span>
                <span
                  className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                    p.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {p.status}
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <div className="text-xs text-gray-500">
                  Used {p.usedSessions}/{p.totalSessions} | Paid {formatUsd(p.paymentAmount)} |
                  Expires {formatPtDate(p.expiresAt)}
                </div>
                {p.refundableAmount > 0 && (
                  <button
                    onClick={() => setRefundTarget(p)}
                    className="text-xs text-red-600 hover:underline whitespace-nowrap"
                  >
                    Refund remaining
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {refundTarget && (
        <RefundDialog
          title="Refund Remaining Pass Sessions"
          summary={`${localize(refundTarget.titleCN, refundTarget.titleEN, siteLanguage)}, ${refundTarget.remainingSessions} of ${refundTarget.totalSessions} sessions remaining`}
          amountCents={refundTarget.refundableAmount}
          url={`${API_URL}/admin/user-passes/${refundTarget.id}/refund`}
          onClose={() => setRefundTarget(null)}
          onSuccess={handleRefunded}
        />
      )}
    </Section>
  );
}

function RefundDialog({
  title,
  summary,
  amountCents,
  zeroAmountHint,
  url,
  onClose,
  onSuccess,
}: {
  title: string;
  summary: string;
  amountCents: number;
  zeroAmountHint?: string;
  url: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { getToken } = useAuth();
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedNote = note.trim();
  const isValidNote = trimmedNote.length >= 5 && trimmedNote.length <= 500;
  const canSubmit = isValidNote && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setError(null);
      const token = await getToken();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: trimmedNote }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Refund failed');
      }
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Refund failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-4">{summary}</p>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            {amountCents > 0 ? (
              <p className="text-sm text-amber-800">
                <strong>{formatUsd(amountCents)}</strong> will be added to the user's store credit.
                The user will receive a notification email.
              </p>
            ) : (
              <p className="text-sm text-amber-800">{zeroAmountHint}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (required, internal only)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. user requested refund, approved by admin"
              rows={3}
              maxLength={500}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <div className="flex justify-between mt-1">
              {note && !isValidNote && (
                <p className="text-xs text-red-500">Note must be 5-500 characters</p>
              )}
              <p className="text-xs text-gray-400 ml-auto">{trimmedNote.length}/500</p>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {submitting
                ? 'Refunding...'
                : amountCents > 0
                  ? `Refund ${formatUsd(amountCents)}`
                  : 'Confirm Refund'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
